<template>
  <div class="settings-page">
    <div class="page-title-bar">
      <h1 class="page-title">Settings</h1>
    </div>

    <section class="settings-card" aria-labelledby="timezone-heading">
      <h2 id="timezone-heading" class="settings-heading">时区</h2>
      <p class="settings-note">
        排程页右侧的日期和时段是否已经过去，按这里选择的时区判断。预约时间本身仍按接口给出的时刻显示。
      </p>

      <div class="timezone-options" role="radiogroup" aria-labelledby="timezone-heading">
        <label
          v-for="zone in APP_TIMEZONES"
          :key="zone.id"
          class="timezone-option"
          :class="{ 'timezone-option--active': appTimeZone === zone.id }"
        >
          <input
            class="timezone-radio"
            type="radio"
            name="app-timezone"
            :value="zone.id"
            :checked="appTimeZone === zone.id"
            @change="onSelect(zone.id)"
          />
          <span class="timezone-copy">
            <span class="timezone-label">{{ zone.label }}</span>
            <span class="timezone-detail">{{ zone.detail }}</span>
          </span>
        </label>
      </div>

      <p class="timezone-now">当前时间：{{ nowLabel }}</p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  APP_TIMEZONES,
  appTimeZone,
  setAppTimeZone,
  zonedNowParts,
  type AppTimeZoneId,
} from '@/utils/appTimezone'

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

const nowLabel = computed(() => {
  const parts = zonedNowParts(appTimeZone.value)
  const hour = Math.floor(parts.minutes / 60)
  const minute = parts.minutes % 60
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)} ${pad2(hour)}:${pad2(minute)}`
})

function onSelect(id: AppTimeZoneId): void {
  setAppTimeZone(id)
}
</script>

<style scoped>
.settings-page {
  box-sizing: border-box;
  padding: 24px 28px 32px;
}

.page-title {
  margin: 0 0 20px;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #000;
}

.settings-card {
  max-width: 520px;
  padding: 20px 22px 18px;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #fff;
}

.settings-heading {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #111;
}

.settings-note {
  margin: 8px 0 16px;
  font-size: 13px;
  line-height: 20px;
  color: #666;
}

.timezone-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.timezone-option {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
}

.timezone-option--active {
  border-color: #111;
  background: #f7f7f7;
}

.timezone-radio {
  margin: 0;
  accent-color: #111;
}

.timezone-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.timezone-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #111;
}

.timezone-detail {
  font-size: 12px;
  line-height: 18px;
  color: #777;
}

.timezone-now {
  margin: 14px 0 0;
  font-size: 13px;
  line-height: 20px;
  color: #333;
}
</style>
